import React, {useEffect} from 'react';
import {Header, Label, Text} from 'react-aria-components';
import FullWidthLayout from "../../layouts/FullWidthLayout/FullWidthLayout";
import DefaultButton from "../../components/Buttons/DefaultButton/DefaultButton";
import {LuAlertTriangle, LuArrowBigRightDash, LuClock, LuCopy, LuEye, LuKey, LuPencil, LuTrash2} from "react-icons/lu";
import {PrismLight as SyntaxHighlighter} from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import {dark, duotoneLight} from 'react-syntax-highlighter/dist/esm/styles/prism';
import LabeledSection from "../../components/LabeledSection/LabeledSection.js";
import {Navigate, useParams} from "react-router-dom";
import FiltersList from "../../components/FiltersList/FiltersList.js";
import api from "../../utils/api.js";
import apiClient from "../../utils/api.js";
import Notification from "../../components/Notification/Notification.js";
import LoadingLayout from "../../layouts/LoadingLayout/LoadingLayout.js";
import TimerProgressBar from "../../components/TimerProgressBar/TimerProgressBar.js";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner.js";
import "./DatabaseDetails.css"
import {FilterResponse, HostedProxyResponse, ServerResponse} from "../../utils/api-client/common.js";
import DatabasePreview, {Message, useWebSocket} from "../../components/DatabasePreview/DatabasePreview.js";
import {DatabaseResponse} from "../../utils/api-client/index.js";


SyntaxHighlighter.registerLanguage('bash', bash);

function getApiKeyStr(apiKey, apiKeys, unencryptedApiKeys) {
    let isLast = false
    if (apiKeys.length > 0 && apiKeys[apiKeys.length-1].id === apiKey.id) {
        isLast = true
    }

    const unencryptedKey = unencryptedApiKeys[apiKey.id]
    if (isLast && unencryptedKey !== undefined) {
        return unencryptedKey
    }
    return `${apiKey.keyPrefix}************${apiKey.keyPostfix}`
}


function APIKeysList({serverId, apiKeys, setApiKeys, unencryptedApiKeys}: { serverId:string, apiKeys: never, setApiKeys: (never) => void, unencryptedApiKeys: Set<string> }) {
    if (apiKeys === undefined || apiKeys.length === 0) {
        return <Text>No API Keys have been created yet.</Text>
    }

    function deleteKey(id) {
        api.deleteApiKey(serverId, id).then(() => {
            apiKeys = apiKeys.filter((key) => key.id !== id)
            setApiKeys(apiKeys)
        })
    }

    const rows = apiKeys.map((apiKey) => {
        const keyStr = getApiKeyStr(apiKey, apiKeys, unencryptedApiKeys)
        return <div key={apiKey.id} className="api-key-row">
            <div className="api-key-row-section name">{apiKey.name}</div>
            <div className="api-key-row-section description">{apiKey.description}</div>
            <div className="api-key-row-section key">{keyStr}</div>
            <div className="api-key-row-section actions">
                {!keyStr.includes("*")?
                    <div className="copy-icon">
                    <LuCopy onClick={() =>
                    {navigator.clipboard.writeText(keyStr)}
                    }/>
                    </div>
                : null
                }
                <div className="delete-icon">
                <LuTrash2 onClick={() => {
                    deleteKey(apiKey.id)
                }}/>
                </div>
            </div>
        </div>
    })

    return <div className="api-keys-list">
        {rows}
    </div>
}


function GeneralSettings({server, hostedProxy}:{server:DatabaseResponse, hostedProxy?: HostedProxyResponse}) {
    const [redirect, setRedirect] = React.useState(null)

    if (redirect !== null) {
        return <Navigate to={redirect} />
    }

    let allowedRolesText = "Inaccessible to all users"
    if (server.roles.length > 0) {
        allowedRolesText = server.roles.map(role => role.name).join(", ")
    }

    let dbUri = "unset"
    if (server.databaseConnection) {
        dbUri = `postgresql://${server.databaseConnection.username}:***********@${server.databaseConnection.host}:${server.databaseConnection.port}/${server.databaseConnection.databaseName}`
    }

    let allowedSchemas = ""
    if (server.allowedSchemas !== null && server.allowedSchemas.length > 0) {
        allowedSchemas = server.allowedSchemas.join(", ")
    }

    let userNotification = null
    let hostedProxyUri = null
    if(hostedProxy) {
        const usernames = server.usernames
        const firstUsername = usernames.length > 0 ? usernames[0] : null
        if (firstUsername === null) {
            hostedProxyUri = <>postgresql://{server.id}.proxy.veilstream.com:5432/{hostedProxy.dbDatabaseName}</>
        } else {
            hostedProxyUri = <>postgresql://{firstUsername}:{"******"}@{server.id}.proxy.veilstream.com:5432/{hostedProxy.dbDatabaseName}</>
        }

        if (usernames.length === 0) {
            userNotification = <Notification type="warning">No Users have access. Make sure at least one Role with Users has access.</Notification>
        }
    }

    return <LabeledSection icon={<LuPencil/>} label="General Settings">
        <table className="database-general-settings">
            <tbody>
            { server.hostingMode === "cloud" ?
                <tr>
                    <td className="key"><Label>Database URI:</Label></td>
                    <td className="val">{dbUri}</td>
                </tr> : null
            }
            {
                server && server.allowedSchemasEnabled ? <tr>
                    <td className="key"><Label>Allowed Schemas:</Label></td>
                    <td className="val">{allowedSchemas}</td>
                </tr> : null
            }
            <tr>
                <td className="key"><Label>On Schema Change:</Label></td>
                <td className="val">{server.onSchemaChange}</td>
            </tr>
            <tr>
                <td className="key"><Label>Number of Tables:</Label></td>
                <td className="val table-count">{server.tablesCount}</td>
            </tr>
            <tr>
                <td className="key"><Label>Roles with Access:</Label></td>
                <td className="val">{allowedRolesText}</td>
            </tr>
            { hostedProxyUri ? <>
                <tr>
                    <td className="key"><Label>Proxy URI:</Label></td>
                    <td className="val proxy-uri"><div>{hostedProxyUri}</div></td>
                </tr>
            </> : null}
            </tbody>
        </table>

        {userNotification}

        <div className="api-key-actions">
            <DefaultButton
                text="Edit"
                className="float-right"
                onPress={() => {setRedirect(`/database/${server.id}/general`)}}
            />
        </div>
    </LabeledSection>

}

function _getProgress(server: ServerResponse, newHostedProxy: HostedProxyResponse) {
    if(newHostedProxy === null || newHostedProxy === undefined) {
        return
    }

    const now = Date.now()
    const updatedAt = newHostedProxy.statusUpdatedAt + "Z"
    const start = new Date(updatedAt).getTime()
    const end = start + 1 * 60 * 1000

    let newProgress = Math.min((now - start) / (end - start), 1) * 100
    if (now > end) {
        newProgress = 100
    }
    if (newProgress < 100 && server.tablesCount === 0) {
        return newProgress
    }
    return 100
}

function ConnectionDetails({server, hostedProxy}: { server: ServerResponse, hostedProxy ?: HostedProxyResponse}) {

    const usernames = server.usernames
    let userNotification = null
    const firstUsername = usernames.length > 0 ? usernames[0] : null
    if (usernames.length === 0) {
        userNotification = <Notification type="warning">No Users have access. Make sure at least one Role with Users has access.</Notification>
    } else if (usernames.length === 1) {
        userNotification = <div className="usernames">with user: <b>{usernames[0]}</b></div>
    } else {
        const usernamesStr = usernames.join(", ")
        userNotification = <div className="usernames">with any of the following users: <b>{usernamesStr}</b></div>
    }

    const connectionString = <div className="connection-string">
        <Text>postgresql://{firstUsername}:{"***your password***"}@{server.id}.proxy.veilstream.com:5432/{hostedProxy.dbDatabaseName}</Text>
    </div>

    return <>
        {connectionString}
        {userNotification}
    </>

}

function getStateShowPreview(serverId) {
    const state = localStorage.getItem(`showPreview-${serverId}`)
    if (state === null || state === undefined) {
        return false
    }
    const val = JSON.parse(state)
    console.log(val)
    return val
}

function saveStateShowPreview(serverId, state:boolean) {
    localStorage.setItem(`showPreview-${serverId}`, JSON.stringify(state))
}

function DatabaseProvisioningCloudHosted({defaultServer, setDefaultServer}:{defaultServer: ServerResponse, setDefaultServer?: (ServerResponse) => void}) {
    const [server, setServer] = React.useState(defaultServer)
    const [hostedProxy, setHostedProxy] = React.useState(null)
    const [progress, setProgress] = React.useState(0)

    useEffect(() => {
        apiClient.getHostedProxy(server.id).then(newHostedProxy => {
            setHostedProxy(newHostedProxy);
        })
    }, []);

    useEffect(() => {
        if (hostedProxy === null || hostedProxy === undefined) {
            return
        }

        const interval = setInterval(() => {
            apiClient.getDatabase(server.id).then(newServer => {
                if (newServer === null || newServer === undefined) {
                    return
                }
                const newProgress = _getProgress(newServer, hostedProxy);

                if (newProgress === 100) {
                    clearInterval(interval);
                    if (setDefaultServer) {
                        setDefaultServer(newServer);
                    }
                }

                setServer(newServer);
                setProgress(newProgress);
            })
        }, 3000);

        return () => clearInterval(interval); // cleanup on unmount
    }, [server, hostedProxy]);

    const content = <LabeledSection icon={<LuArrowBigRightDash/>} label="Provisioning">
        <div id="database-edit" >
            <div className="spinner-wrapper">
                <LoadingSpinner />
            </div>
            <TimerProgressBar progress={progress} text="provisioning..." />
            <Notification>{"We're provisioning a proxy for your database now. It will take a moment to turn on, then you will be able to connect to to it."}</Notification>
        </div>
    </LabeledSection>

    return <FullWidthLayout
        title={server.name}
        mainContent={content}
    />
}


function DatabaseEditCloudHosted({defaultServer}:{defaultServer ?: {
        updatedAt: any;
        id: string, name: string, databaseConnection: never, hostingMode: string, tablesCount: number, status: string, statusReason: string, filters: Array<FilterResponse> }}) {

    const [server, setServer] = React.useState(defaultServer)
    const [pollServer, setPollServer] = React.useState(0)

    const [showPreview, setShowPreview] = React.useState(getStateShowPreview(server.id))
    const [hostedProxy, setHostedProxy] = React.useState(null)
    const [pollHostedProxy, setPollHostedProxy] = React.useState(0)

    useEffect(() => {
        api.getDatabase(server.id).then(newServer => {
            if (newServer.status !== "initial") {
                setServer(newServer)
            }
            if(newServer.tablesCount === 0 && newServer.status === "initial") {
                setTimeout(() => {
                    setPollServer(pollServer + 1)
                }, 3000)
            }
        })
    }, [pollServer, server.id]);

    useEffect(() => {
        api.getHostedProxy(server.id).then(newHostedProxy => {
            setHostedProxy(newHostedProxy)
            if (newHostedProxy.status !== "initial") {
                setPollHostedProxy(0)
            } else {
                setTimeout(() => {
                    setPollHostedProxy(pollHostedProxy + 1)
                }, 5000)
            }
        })
    }, []);

    if (server === null || server === undefined || hostedProxy === null || hostedProxy === undefined) {
        return <LoadingLayout />
    }

    let content = null;
    if(server.status === "error"){
        content = <>
            <GeneralSettings server={server} hostedProxy={hostedProxy} />
            <LabeledSection icon={<LuAlertTriangle/>} label="Error Launching">
                <Notification type="warning">
                    {server.statusReason}
                </Notification>
            </LabeledSection>
        </>
    } else {
        content = <div id="database-edit">
            <GeneralSettings server={server} hostedProxy={hostedProxy} />
            <FiltersList
                server={server}
                filters={server.filters}
                returnTo={`/database/${server.id}`}
                setFilters={(newFilters) => {
                    setServer({...server, filters: newFilters})
                }}
            />
        </div>
    }

    let isActive = false
    if (server.isActive === true) {
        isActive = true
    } else {
        if(showPreview){
            setShowPreview(false)
        }
    }
    const togglePreviewButton = <DefaultButton
        text="Preview"
        onPress={() => {
            setShowPreview(!showPreview)
            saveStateShowPreview(server.id, !showPreview)
        }}
        className={"float-right"+(isActive ? "" : " disabled")}
        disabled={!isActive}
    />

    function serverConfigHash(server) {
        let hashData = ''
        for (const filter of server.filters) {
            hashData += filter.id + filter.name + filter.conditions.map(c => c.column + c.value).join('')
            hashData += filter.actions.map(a => a.type + a.column + a.value).join('')
        }
        const hash = btoa(hashData)
        console.log("serverConfigHash: ", hash)
        return `${server.id}-${hash}`
    }
    return <FullWidthLayout
        title={server.name}
        actionButton={togglePreviewButton}
        mainContent={content}
        stretched
        sideContent={showPreview?<DatabasePreview key={serverConfigHash(server)} server={server} />:null}
    />
}


function DatabaseEditSelfHosted({defaultServer}:{defaultServer ?: { id: string, name: string, databaseConnection: never, hostingMode: string, tablesCount: number, status: string, statusReason: string, filters: Array<FilterResponse>, updatedAt: any }}) {
    const [server, setServer] = React.useState(defaultServer)
    const [unencryptedApiKeys, setUnencryptedApiKeys] = React.useState({})
    const [pollServer, setPollServer] = React.useState(0)
    const [showPreview, setShowPreview] = React.useState(getStateShowPreview(server.id))
    const [newKeyNotification, setNewKeyNotification] = React.useState(false)
    const [latestDockerImage, setLatestDockerImage] = React.useState(null)
    const {id} = useParams()
    const serverId = id

    useEffect(() => {
        api.getDatabase(serverId).then(newServer => {
            if (newServer.status === "active"){
                setServer(newServer)
            } else {
                setTimeout(() => {
                    setPollServer(pollServer + 1)
                }, 3000)
            }
        })
    }, [pollServer]);

    useEffect(() => {
        api.getLatestDockerImage().then((image) => {
            setLatestDockerImage(image)
        })
    }, []);

    if (server === null || server === undefined) {
        return <LoadingLayout />
    }

    function setApiKeys(newApiKeys) {
        setServer({...server, apiKeys: newApiKeys})
    }

    function createApiKey() {
        return api.createApiKey(serverId).then(newKey => {
            const newUnencryptedApiKeys = unencryptedApiKeys
            newUnencryptedApiKeys[newKey.id] = newKey.unencryptedKey
            setUnencryptedApiKeys(newUnencryptedApiKeys)
            api.getDatabase(serverId).then(newServer =>{
                setServer(newServer)
                setNewKeyNotification(true)
            })
        })
    }

    let bottomSection = <>
        <FiltersList
            filters={server.filters}
            returnTo={`/database/${server.id}`}
            setFilters={(newFilters) => {
                setServer({...server, filters: newFilters});
            }}
            server={server}
        />
    </>
    let topSection = <></>
    if (server.tables.length === 0) {
        let keyStr = "<...>"
        if (server.apiKeys && server.apiKeys.length > 0){
            keyStr = getApiKeyStr(server.apiKeys[server.apiKeys.length-1], server.apiKeys, unencryptedApiKeys)
        }

        let theme = duotoneLight
        const currTheme = localStorage.getItem("theme")
        if(currTheme === "dark") {
            theme = dark
        }

        let imageName = "veilstream/veilstream-postgres-proxy:latest"
        if (latestDockerImage !== null && latestDockerImage !== undefined) {
            imageName = latestDockerImage.fullImageName
        }

        topSection = <LabeledSection icon={<LuClock/>} label="Getting Started">
            <div className="getting-started-steps">
            <Header>1. Create an API Key</Header>
            <div className="getting-started-step">
                <Text>Create an API key with the button below.</Text><br/>
                <div className="note">
                    NOTE:
                    You should make a copy of this key. For security reasons, we only store this API key
                    through a one-way encryption, and cannot recover it for you.
                </div>
            </div>

            <Header>2. Get DB Connection String</Header>
            <div className="getting-started-step">
                <Text>
                    The VeilStream proxy will need access to your database.
                    It needs to read your users and schemas, so that you can configure filters.
                    It also needs to write some stored procedures to a veilstream schema.
                </Text>
                <br/>
            </div>

            <Header>3. Run the proxy beside your database</Header>
            <div className="getting-started-step">
                <Text>Below is how you&apos;d run it with docker</Text>
                <div className="sample-code">
                    <SyntaxHighlighter language="bash" style={theme}>
                        {`docker run \\\n -p 5433:5432 \\\n -e VEILSTREAM_API_KEY=${keyStr} \\\n -e DATABASE_URI=postgresql://user:password@localhost:5432/name \\\n ${imageName}`}
                    </SyntaxHighlighter>
                </div>
                <div className="note">
                    NOTE: You need to run the proxy somewhere that has access to the source database
                </div>
            </div>

            <Header>4. Configure Filters</Header>
            <div className="getting-started-step">
                <Text>After the proxy is running, you can configure filters to modify the data that is
                    returned.</Text><br/>
                <div className="note">
                NOTE: If the user account queries the original database, they will have raw access to the data. To view it through the appropriate filters, they must connect to the proxy
                </div>
            </div>
            </div>
        </LabeledSection>
        bottomSection = null
    }


    const content = <div id="database-edit">
        {topSection}

        <div className="two-even-columns-db">
            <div className="left">
                <GeneralSettings server={server} />
            </div>

            <div className="right">
                <LabeledSection icon={<LuKey/>} label="API Keys">
                    <APIKeysList
                        serverId={server.id}
                        apiKeys={server.apiKeys}
                        setApiKeys={setApiKeys}
                        unencryptedApiKeys={unencryptedApiKeys}
                    />
                    <div className="api-key-actions">
                        <DefaultButton
                            text="Create API Key"
                            onPress={createApiKey}
                            className="float-right"
                            oneClick
                        />
                    </div>
                </LabeledSection>
            </div>
        </div>

        {bottomSection}
    </div>

    let isActive = false
    if (server.isActive === true) {
        isActive = true
    } else {
        if(showPreview){
            setShowPreview(false)
        }
    }
    const togglePreviewButton = <DefaultButton
        text="Preview"
        onPress={() => {
            setShowPreview(!showPreview)
            saveStateShowPreview(server.id, !showPreview)
        }}
        className={"float-right"+(isActive ? "" : " disabled")}
        disabled={!isActive}
    />

    return <FullWidthLayout
        title={server.name}
        actionButton={togglePreviewButton}
        mainContent={content}
        stretched
        sideContent={showPreview?<DatabasePreview key={server.updatedAt} server={server} />:null}
    />
}


function DatabaseDetails() {
    const [server, setServer] = React.useState(null)
    const {id} = useParams()

    useEffect(() => {
        api.getDatabase(id).then(newServer => {
            setServer(newServer)
        })
    }, []);

    if (server === null || server === undefined) {
        return <LoadingLayout />
    }

    if (server.databaseConnection === null || server.databaseConnection === undefined) {
        return <DatabaseEditSelfHosted defaultServer={server} />
    }

    if (!server.isActive && server.hostingMode === "cloud" && server.tablesCount === 0) {
        return <DatabaseProvisioningCloudHosted defaultServer={server} setDefaultServer={setServer} />
    }

    return <DatabaseEditCloudHosted defaultServer={server} />
}


export default DatabaseDetails

